import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpContext,
  HttpEvent,
  HttpEventType,
  HttpProgressEvent,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { AbstractUploadHandler } from './base-upload-handler';
import { MTMCustomFile } from '../mtm-custom-file';
import { TransferStatus } from '../transfer-status';
import { UrlSanitizer } from '../../api.service';
import { IS_UNCANCELABLE_REQUEST } from '../../../models';
import { listenerUploadProgressChanged } from '../listeners';

const chunkSize: number = 16 * 1024 * 1024; //16Mb
const uploadProgressThrottle: number = 2500;

@Injectable({
  providedIn: 'root',
})
export class ChunkedUploadHandler extends AbstractUploadHandler {
  constructor(private readonly http: HttpClient) {
    super();
  }

  public canHandle(file: MTMCustomFile): boolean {
    return file.step === 'chunkUpload';
  }

  public handleUpload(file: MTMCustomFile): void {
    if (file.startIndex == 0) {
      if (!file.sessionUrl) {
        this.initiateResumableUpload(file);
      } else {
        this.uploadFileChunk(file);
      }
    } else {
      this.uploadFileChunk(file);
    }
  }

  /**
   * Starts resumable upload session with Google Cloud Storage
   * @param file - The file to initiate resumable upload for
   */
  private initiateResumableUpload(file: MTMCustomFile): void {
    const initialUrl: string = file.dbFileObject.signedURL;
    const { progress$ } = file;
    const initialUploadUrl: string = initialUrl;
    //don't encode the URL
    const safeurl: string = UrlSanitizer.sanitizeUrl(initialUploadUrl);

    file.currentRequest = this.http
      .post<void>(
        safeurl,
        {},
        {
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
            'x-goog-resumable': 'start',
          },
          observe: 'response',
          context: new HttpContext().set(IS_UNCANCELABLE_REQUEST, true),
        }
      )
      .subscribe({
        next: (response: HttpResponse<void>) => {
          const sessionUrl: string = response.headers.get('Location') || '';
          if (!sessionUrl) {
            progress$.error('no location header for session url from metadata');
            return;
          }
          file.sessionUrl = sessionUrl;
          this.uploadFileChunk(file);
        },
        error: (error: Error) => {
          progress$.error(error);
        },
      });
  }

  /**
   * Uploads file in chunks using resumable upload protocol
   * @param file - The file to upload in chunks
   */
  private uploadFileChunk(file: MTMCustomFile): void {
    const { sessionUrl, startIndex } = file;
    const endIndex: number = Math.min(startIndex + chunkSize, file.size) - 1;
    const buffer: Blob = file.file.slice(startIndex, endIndex + 1);
    const locationResponseRe: RegExp = /bytes=(\d+)-(\d+)/;
    const safeUrl: string = UrlSanitizer.sanitizeUrl(sessionUrl);

    file.currentRequest = this.http
      .put(safeUrl, buffer, {
        headers: {
          'Content-Range': `bytes ${startIndex}-${endIndex}/${file.size}`,
        },
        observe: 'events',
        context: new HttpContext().set(IS_UNCANCELABLE_REQUEST, true),
        reportProgress: true,
      })
      .subscribe({
        next: (response: HttpEvent<ArrayBuffer>) => {
          this.handleChunkResponse(file, response, endIndex);
        },
        error: (error: HttpErrorResponse) => {
          this.handleChunkError(file, error, startIndex, locationResponseRe);
        },
      });
  }

  private handleChunkResponse(file: MTMCustomFile, response: HttpEvent<ArrayBuffer>, endIndex: number): void {
    switch (response.type) {
      case HttpEventType.UploadProgress:
        this.handleChunkProgress(file, response);
        break;
      case HttpEventType.Response:
        this.handleChunkComplete(file, endIndex);
        break;
      case HttpEventType.Sent:
      case HttpEventType.ResponseHeader:
      case HttpEventType.DownloadProgress:
      case HttpEventType.User:
        break;
    }
  }

  private handleChunkProgress(file: MTMCustomFile, response: HttpProgressEvent): void {
    const curTimestamp: number = new Date().valueOf();
    if (curTimestamp - file.lastProgressTime < uploadProgressThrottle) {
      return;
    }

    let currentUploaded: number = file.startIndex + response.loaded;
    if (currentUploaded > file.fileSize) {
      currentUploaded = file.fileSize;
    }
    const newPercentage: number = Math.floor((currentUploaded * 100) / file.fileSize);
    file.extraInfo.totalUploaded = currentUploaded;
    if (newPercentage === file.progress) {
      return;
    }
    file.progress = newPercentage;
    file.uploadStatusCode = TransferStatus.SENDING;
    listenerUploadProgressChanged.next(file);
    file.lastProgressTime = curTimestamp;
  }

  private handleChunkComplete(file: MTMCustomFile, endIndex: number): void {
    file.progress = 100;
    file.retryCount = 0;
    listenerUploadProgressChanged.next(file);
    this.emitProgress(file, {
      status: TransferStatus.UPLOADED,
      startIndex: endIndex,
      step: 'next',
    });
  }

  private handleChunkError(
    file: MTMCustomFile,
    error: HttpErrorResponse,
    startIndex: number,
    locationResponseRe: RegExp
  ): void {
    if (error.status === 308) {
      const rangeResp: string = error.headers.get('Range') || '';
      const match: RegExpMatchArray | null = rangeResp.match(locationResponseRe);
      if (!match || match.length < 3) {
        file.progress$.error('failed to get range header');
        return;
      }
      file.retryCount = 0;
      const nextStart: number = parseInt(match[2].toString(), 10);
      this.emitProgress(file, {
        status: TransferStatus.SENDING,
        startIndex: nextStart,
        step: '',
      });
      return;
    }
    this.emitProgress(file, {
      status: TransferStatus.ERROR,
      startIndex,
      error: error.message,
      step: 'error',
    });
  }
}
